import { useEffect, useMemo } from 'react';
import AppRoutes from './routes/AppRoute';
import { App as AntApp, ConfigProvider, theme } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import themeDarkConfig from './theme/themeDarkConfig';
import themeLightConfig from './theme/themeLightConfig';
import { isDarkThemeMode } from './theme/themeMode';
import { NotificationProvider } from './utils/NotificationService';
import LanguageRouteSync from './routes/LanguageRouteSync';
import {
  DEFAULT_LANGUAGE,
  getSupportedLanguage,
  isRtlLanguage,
  LANGUAGE,
} from './i18nLanguages';
import './styles/App.css';

function App() {
  const { i18n } = useTranslation();
  const themeMode = useSelector((state) => state.currentUser?.themeMode);
  const isDark = isDarkThemeMode(themeMode);
  const language =
    getSupportedLanguage(i18n.resolvedLanguage || i18n.language) ||
    DEFAULT_LANGUAGE;
  const antdLocale = language === LANGUAGE.AR ? arEG : enUS;

  useEffect(() => {
    const nextTheme = isDark ? 'dark' : 'light';
    const themeColor = document.querySelector('meta[name="theme-color"]');

    document.documentElement.dataset.theme = nextTheme;
    document.documentElement.style.colorScheme = nextTheme;
    themeColor?.setAttribute('content', isDark ? '#07101f' : '#f5f7fb');
  }, [isDark]);

  const themeApp = useMemo(
    () => ({
      algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: isDark ? themeDarkConfig.token : themeLightConfig.token,
      components: isDark
        ? themeDarkConfig.components
        : themeLightConfig.components,
    }),
    [isDark]
  );

  return (
    <ConfigProvider
      theme={themeApp}
      locale={antdLocale}
      direction={isRtlLanguage(language) ? 'rtl' : 'ltr'}
    >
      <AntApp>
        <NotificationProvider>
          <BrowserRouter
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <LanguageRouteSync />
            <AppRoutes />
          </BrowserRouter>
        </NotificationProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
